import * as React from "react";
import {
    Box,
    Typography,
    SxProps,
    Theme,
    CardMedia,
    Card,
    CardContent,
    IconButton,
    Grid,
} from "@mui/material";
import { useIsMobile } from "../hooks";
import { AboutUsPerson } from "../types";
import ArrowPreviousIcon from "./icons/ArrowPreviousIcon";
import ArrowNextIcon from "./icons/ArrowNextIcon";
import { useTranslation } from "react-i18next";

export type PersonCardProps = {
    person: AboutUsPerson;
};

const smoothScrollTo = (
    element: HTMLElement,
    targetLeft: number,
    duration = 420
) => {
    const start = element.scrollLeft;
    const distance = targetLeft - start;
    const startTime = performance.now();

    const ease = (t: number) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const animate = (now: number) => {
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / duration);
        element.scrollLeft = start + distance * ease(t);

        if (t < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
};

const PersonCardStyle: Record<string, SxProps<Theme>> = {
    cardHeaderContainer: (theme: Theme) => ({
        background: theme.palette.secondary.main,
        padding: "10px",
    }),
    cardTitle: { fontSize: "24px" },
    cardSubtitle: { fontSize: "18px" },
    cardDescription: { fontSize: "20px", marginTop: "20px", textAlign: "left" },
};

const PersonCard: React.FC<PersonCardProps> = ({ person }) => {
    const { i18n } = useTranslation();

    return (
        <Card sx={{ maxWidth: 330, background: "transparent" }}>
            <CardMedia
                sx={{
                    height: 300,
                    border: "3px solid white",
                    backgroundPosition: "top",
                }}
                image={person.image}
            />
            <CardContent>
                <Box sx={PersonCardStyle.cardHeaderContainer}>
                    <Typography sx={PersonCardStyle.cardTitle}>
                        {person.name}
                    </Typography>
                    <Typography sx={PersonCardStyle.cardSubtitle}>
                        {person.position}
                    </Typography>
                </Box>

                <Typography sx={PersonCardStyle.cardDescription}>
                    {i18n.resolvedLanguage === "en"
                        ? person.descriptionEn
                        : person.descriptionIt}
                </Typography>
            </CardContent>
        </Card>
    );
};

const PeopleCarouselStyle: Record<string, SxProps<Theme>> = {
    container: { position: "relative", width: "100%" },

    sliderContainer: {
        position: "relative",
        overflowX: "auto",
        overflowY: "hidden",
        scrollBehavior: "auto",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",         // Firefox
        msOverflowStyle: "none",        // Internet Explorer + Edge (older)
        "&::-webkit-scrollbar": {
            display: "none",              // Chrome / Safari
        },
    },

    sliderPage: {
        display: "flex",
        alignItems: "start",
    },

    arrowLeft: {
        position: "absolute",
        left: "-80px",
        top: "200px",
        width: "80px",
        zIndex: 1000,
    },

    arrowRight: {
        position: "absolute",
        right: "-80px",
        top: "200px",
        width: "80px",
        zIndex: 1000,
    },
};

export type CarouselProps = {
    items: AboutUsPerson[];
};

const PeopleCarousel: React.FC<CarouselProps> = ({ items }) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [slideWidth, setSlideWidth] = React.useState(0);
    const { isMobile, windowWidth } = useIsMobile(1200);

    const goNext = () => {
        const container = containerRef.current;
        if (!container) return;

        const maxScroll = container.scrollWidth - container.clientWidth;
        let nextVal = container.scrollLeft + slideWidth;
        
        if (nextVal > maxScroll) {
            // a volte veniva skippata l'ultima card, quindi controllo che venga superata del 95%
            if (nextVal > maxScroll + 0.95 * slideWidth) {
                nextVal = 0;
            }
            else {
                nextVal = maxScroll;
            }
        };

        smoothScrollTo(container, nextVal);
    };

    const goPrevious = () => {
        const container = containerRef.current;
        if (!container) return;

        let prevVal = container.scrollLeft - slideWidth;
        if (prevVal < 0) {
            prevVal = container.scrollWidth - container.clientWidth;
        };

        smoothScrollTo(container, prevVal);
    };

    React.useEffect(() => {
        const container = containerRef.current;
        setSlideWidth(container ? container.clientWidth / 3 : 0);
    }, [isMobile, windowWidth]);

    return (
        <Box sx={PeopleCarouselStyle.container}>
            {!isMobile && (
                <>
                    <IconButton disableRipple sx={PeopleCarouselStyle.arrowLeft} onClick={goPrevious}>
                        <ArrowPreviousIcon sx={{ width: "100%", height: "auto" }} />
                    </IconButton>

                    <IconButton disableRipple sx={PeopleCarouselStyle.arrowRight} onClick={goNext}>
                        <ArrowNextIcon sx={{ width: "100%", height: "auto" }} />
                    </IconButton>

                    <Box sx={PeopleCarouselStyle.sliderContainer} ref={containerRef}>
                        <Box
                            sx={[
                                PeopleCarouselStyle.sliderPage,
                                { width: slideWidth * items.length },
                            ].flat()}
                        >
                            {items.map((_person) => (
                                <Box
                                    key={_person.name}
                                    sx={{
                                        width: slideWidth,
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                >
                                    <PersonCard person={_person} />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </>
            )}

            {isMobile && (
                <Grid container spacing={4} justifyContent="center">
                    {items.map((_person) => (
                        <Grid item sm={12} md={6} key={_person.name} display="flex" justifyContent="center">
                            <PersonCard person={_person} />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default PeopleCarousel;
